import { useEffect, useState, type FormEvent } from 'react';
import type {
  PhotoIdentification,
  Product,
  ProductInput,
  StockEntryInput,
  StorageSummary,
} from '@almox/shared';
import {
  ProductFields,
  applyVisionSuggestion,
  changedProductFields,
  draftToProductInput,
  needsBarcodeLink,
  productToDraft,
  type ProductDraft,
} from './ProductFields.js';
import { hasPack, quantityFromPacks } from '@almox/shared';
import { storageEmoji, useToast } from './ui.js';

interface Props {
  draft: ProductDraft;
  onDraftChange: (next: ProductDraft) => void;
  /** Produto do catalogo antes das edicoes, quando ele ja existia. */
  original: Product | null;
  storages: StorageSummary[];
  defaultStorageId: string | null;
  submitting: boolean;
  /**
   * `productPatch` so vem quando o produto ja estava no catalogo e o usuario
   * corrigiu algo — o chamador atualiza o catalogo antes de guardar.
   */
  onSubmit: (input: StockEntryInput, productPatch?: Partial<ProductInput>) => void;
  onCancel: () => void;
  /** Liga a camera da tela para preencher o codigo de barras. */
  onScanBarcode?: () => void;
  scanning?: boolean;
  autoFocusProduct?: boolean;
}

/**
 * Formulario de entrada (regra 1). O produto entra em campos separados — marca,
 * tipo, tamanho, categoria — e nenhum deles e obrigatorio: basta ter como
 * chamar o produto (CLAUDE.md secao 8). O bloco do produto fica recolhido
 * quando ele ja e conhecido, para a entrada continuar em dois toques.
 */
export function EntryForm({
  draft,
  onDraftChange,
  original,
  storages,
  defaultStorageId,
  submitting,
  onSubmit,
  onCancel,
  onScanBarcode,
  scanning,
  autoFocusProduct,
}: Props) {
  const toast = useToast();
  const [storageId, setStorageId] = useState(defaultStorageId ?? storages[0]?.id ?? '');
  const [quantity, setQuantity] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [restock, setRestock] = useState(true);
  const known = Boolean(draft.productId);
  // Embalagem com varias unidades: o usuario conta embalagens, o estoque conta
  // unidades — e so assim a baixa pode ser de um ovo por vez.
  const porEmbalagem = Number(draft.packSize);
  const embalagem = hasPack(porEmbalagem) ? porEmbalagem : null;
  // Produto novo (ou lido pela IA) abre com os campos a vista para conferencia.
  const [detalhes, setDetalhes] = useState(!known);

  useEffect(() => {
    setQuantity(1);
    setExpiresAt('');
    setRestock(true);
    setDetalhes(!draft.productId || draft.origin === 'photo');
    // Trocou de produto: o resto do formulario volta ao padrao.
  }, [draft.productId, draft.origin]);

  useEffect(() => {
    if (!storageId && defaultStorageId) setStorageId(defaultStorageId);
  }, [defaultStorageId, storageId]);

  // Vinculo do codigo de barras pedido pela IA: o bloco precisa estar aberto.
  const faltaCodigo = needsBarcodeLink(draft);
  useEffect(() => {
    if (faltaCodigo) setDetalhes(true);
  }, [faltaCodigo]);

  /** Leitura por foto dentro do formulario — funciona ate com codigo ja lido. */
  function aplicarFoto(result: PhotoIdentification, photo: string) {
    if (result.product) {
      // O codigo da foto ja era conhecido: o catalogo tem a palavra final.
      onDraftChange(productToDraft(result.product));
      toast(`${result.product.name} ja estava no seu catalogo.`, 'ok');
      return;
    }
    if (result.suggestion) {
      onDraftChange({
        ...applyVisionSuggestion(draft, result.suggestion, result.barcode),
        photoUrl: draft.photoUrl,
      });
      toast('Embalagem lida — confira os campos.', 'ok');
    }
    void photo;
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!storageId) return;
    if (faltaCodigo) {
      toast('Informe o codigo de barras ou marque que o produto nao tem um.', 'error');
      setDetalhes(true);
      return;
    }

    const produto = draftToProductInput(draft);
    if (!produto.name) {
      toast('Informe ao menos o tipo ou o nome do produto.', 'error');
      return;
    }

    const input = {
      ...(draft.productId ? { productId: draft.productId } : {}),
      ...(draft.barcode.trim() ? { barcode: draft.barcode.trim() } : {}),
      ...(known
        ? {}
        : {
            product: {
              ...produto,
              // Guarda de onde vieram os dados deste produto no catalogo.
              ...(draft.origin === 'photo'
                ? { source: 'photo' as const }
                : draft.origin === 'external'
                  ? { source: 'external' as const }
                  : {}),
            },
          }),
      storageId,
      quantity: quantityFromPacks(quantity, embalagem),
      ...(expiresAt ? { expiresAt } : {}),
      restockOnDepletion: restock,
    } as StockEntryInput;

    // Produto ja no catalogo com algo corrigido (marca, tamanho, unidade):
    // o catalogo e atualizado antes de guardar, senao a entrada nasce errada.
    const patch = original ? changedProductFields(draft, original) : null;
    onSubmit(input, patch ?? undefined);
  }

  return (
    <form onSubmit={handleSubmit}>
      {draft.photoUrl ? (
        <img
          src={draft.photoUrl}
          alt=""
          style={{ maxHeight: 120, borderRadius: 12, marginBottom: 12 }}
        />
      ) : null}

      {known ? (
        <p className="small muted" style={{ marginTop: 0 }}>
          <strong>{draft.name}</strong>
          {draft.brand ? ` · ${draft.brand}` : ''}
          {draft.size ? ` · ${draft.size}` : ''}
          {draft.barcode ? ` · codigo ${draft.barcode}` : ''} — ja no seu catalogo.
        </p>
      ) : null}

      {!known && draft.barcode && draft.origin !== 'photo' ? (
        <p className="small muted" style={{ marginTop: 0 }}>
          Codigo <strong>{draft.barcode}</strong>
          {draft.origin === 'external' ? ' — dados sugeridos pelo catalogo publico' : null}
          {draft.origin === 'none' ? ' — produto novo, complete o cadastro' : null}
        </p>
      ) : null}

      {detalhes ? (
        <ProductFields
          value={draft}
          onChange={onDraftChange}
          idPrefix="entry"
          {...(onScanBarcode ? { onScanBarcode } : {})}
          {...(scanning !== undefined ? { scanning } : {})}
          {...(autoFocusProduct !== undefined ? { autoFocus: autoFocusProduct } : {})}
          onPhotoResult={aplicarFoto}
        />
      ) : (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setDetalhes(true)}
          style={{ marginBottom: 12 }}
        >
          Editar produto (marca, tipo, tamanho…)
        </button>
      )}

      <div className="row">
        <div className="field">
          <label htmlFor="entry-storage">Dispensa</label>
          <select
            id="entry-storage"
            value={storageId}
            onChange={(event) => setStorageId(event.target.value)}
            required
          >
            {storages.length === 0 ? <option value="">Crie uma dispensa primeiro</option> : null}
            {storages.map((storage) => (
              <option key={storage.id} value={storage.id}>
                {storageEmoji(storage.icon)} {storage.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="entry-qty">
            {embalagem ? `Embalagens (${embalagem} ${draft.unit} cada)` : `Quantidade (${draft.unit})`}
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-icon"
              onClick={() => setQuantity((q) => Math.max(1, Math.round((q - 1) * 100) / 100))}
              aria-label="Diminuir quantidade"
            >
              −
            </button>
            <input
              id="entry-qty"
              type="number"
              min={0.01}
              step="any"
              value={quantity}
              onChange={(event) => setQuantity(Number(event.target.value))}
              style={{ textAlign: 'center' }}
            />
            <button
              type="button"
              className="btn btn-icon"
              onClick={() => setQuantity((q) => Math.round((q + 1) * 100) / 100)}
              aria-label="Aumentar quantidade"
            >
              +
            </button>
          </div>
          {embalagem ? (
            <span className="small muted">
              Entra no estoque como {quantityFromPacks(quantity, embalagem)} {draft.unit} — a baixa
              sai de uma em uma.
            </span>
          ) : null}
        </div>
      </div>

      <div className="field">
        <label htmlFor="entry-expiry">Validade (opcional)</label>
        <input
          id="entry-expiry"
          type="date"
          value={expiresAt}
          onChange={(event) => setExpiresAt(event.target.value)}
        />
        <span className="small muted">
          Em branco, o Almox estima pela validade padrao da categoria e marca com “~”.
        </span>
      </div>

      <label className="check">
        <input
          type="checkbox"
          checked={restock}
          onChange={(event) => setRestock(event.target.checked)}
        />
        Ao acabar, mandar para a lista de compras
      </label>

      <div className="modal-actions">
        <button type="button" className="btn" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting || !storageId}>
          {submitting ? 'Salvando…' : 'Dar entrada'}
        </button>
      </div>
    </form>
  );
}
